import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './JamaReferralSection.css';

/* ═══════════════════════════════════════════════════════════════
   JAMA Referral Section — Kurzgesagt style
   Animated story showing how referral links work
   ═══════════════════════════════════════════════════════════════ */

const TEAL = '#1BC7B1';
const ORANGE = '#FF6A00';
const BLUE = '#6C7AE0';
const PINK = '#E85D75';
const EYE = '#1A1A2E';
const W = '#FFFFFF';
const DK = '#2D2D3A';
const SK1 = '#F4A4B0';
const SK3 = '#E8C4A0';
const HR1 = '#5B3D8E';
const HR3 = '#D4543A';

const steps = [
    { id: 'share', label: 'Comparte tu link', icon: '1' },
    { id: 'order', label: 'Tu amigo pide', icon: '2' },
    { id: 'earn', label: 'Tu ganas', icon: '3' },
];

const STEP_DURATION = 4000;

/* ═══ PERSON A — The referrer (purple hair, phone sharing link) ═══ */
function PersonSharing({ animate }) {
    return (
        <svg viewBox="0 0 160 280" className="ref-char">
            <motion.g
                initial={{ y: 15, opacity: 0 }}
                animate={animate ? { y: 0, opacity: 1 } : {}}
                transition={{ type: 'spring', stiffness: 200, damping: 20 }}
            >
                <ellipse cx="80" cy="268" rx="36" ry="6" fill="rgba(27,199,177,0.12)" />

                {/* Left arm (behind) */}
                <rect x="22" y="96" width="22" height="44" rx="11" fill={TEAL} />
                <rect x="24" y="132" width="18" height="30" rx="9" fill={SK1} />
                <ellipse cx="33" cy="164" rx="9" ry="8" fill={SK1} />

                {/* Body */}
                <rect x="40" y="86" width="72" height="82" rx="14" fill={TEAL} />
                <rect x="80" y="86" width="32" height="82" rx="10" fill="rgba(0,0,0,0.06)" />
                <path d="M64 86 L76 100 L88 86" fill="#15A89D" />

                {/* Legs */}
                <rect x="46" y="162" width="26" height="76" rx="12" fill={BLUE} />
                <rect x="80" y="162" width="26" height="76" rx="12" fill={BLUE} />
                <rect x="80" y="162" width="26" height="76" rx="12" fill="rgba(0,0,0,0.04)" />

                {/* Shoes */}
                <ellipse cx="59" cy="242" rx="16" ry="8" fill={DK} />
                <ellipse cx="93" cy="242" rx="16" ry="8" fill={DK} />

                {/* Right arm + phone (animated) */}
                <motion.g
                    animate={animate ? { rotate: [-3, 3, -3] } : {}}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                    style={{ transformOrigin: '118px 104px' }}
                >
                    <rect x="106" y="96" width="22" height="42" rx="11" fill={TEAL} />
                    <rect x="108" y="130" width="18" height="28" rx="9" fill={SK1} />
                    <ellipse cx="117" cy="160" rx="9" ry="8" fill={SK1} />
                    {/* Phone */}
                    <rect x="107" y="142" width="20" height="32" rx="5" fill={DK} />
                    <rect x="110" y="146" width="14" height="22" rx="3" fill="#4A90D9" />
                    {/* Link icon on phone */}
                    <circle cx="117" cy="154" r="3.5" fill={TEAL} />
                    <rect x="115" y="160" width="4" height="3" rx="1" fill={TEAL} />
                </motion.g>

                {/* Neck */}
                <rect x="68" y="68" width="18" height="20" rx="4" fill={SK1} />

                {/* Head */}
                <ellipse cx="77" cy="44" rx="24" ry="26" fill={SK1} />
                <ellipse cx="53" cy="48" rx="4" ry="6" fill={SK1} />
                <ellipse cx="101" cy="48" rx="4" ry="6" fill={SK1} />

                {/* Hair */}
                <ellipse cx="77" cy="24" rx="28" ry="15" fill={HR1} />
                <ellipse cx="63" cy="30" rx="12" ry="11" fill={HR1} />
                <ellipse cx="91" cy="28" rx="10" ry="10" fill={HR1} />
                <rect x="49" y="38" width="11" height="38" rx="5.5" fill={HR1} />
                <rect x="94" y="38" width="11" height="38" rx="5.5" fill={HR1} />

                {/* Face */}
                <circle cx="68" cy="44" r="3" fill={EYE} />
                <circle cx="70" cy="42" r="1" fill={W} />
                <circle cx="86" cy="44" r="3" fill={EYE} />
                <circle cx="88" cy="42" r="1" fill={W} />
                <ellipse cx="62" cy="52" rx="6" ry="3" fill="rgba(232,93,117,0.2)" />
                <ellipse cx="92" cy="52" rx="6" ry="3" fill="rgba(232,93,117,0.2)" />
                <rect x="73" y="54" width="8" height="4" rx="2" fill={PINK} />
                <rect x="75" y="54" width="4" height="2" rx="1" fill={W} />
            </motion.g>
        </svg>
    );
}

/* ═══ PERSON B — Friend who orders (red hair, receiving link) ═══ */
function PersonOrdering({ animate }) {
    return (
        <svg viewBox="0 0 160 280" className="ref-char">
            <motion.g
                initial={{ y: 15, opacity: 0 }}
                animate={animate ? { y: 0, opacity: 1 } : {}}
                transition={{ type: 'spring', stiffness: 200, damping: 20, delay: 0.2 }}
            >
                <ellipse cx="80" cy="268" rx="36" ry="6" fill="rgba(255,106,0,0.1)" />

                {/* Left arm */}
                <rect x="22" y="96" width="22" height="44" rx="11" fill={ORANGE} />
                <rect x="24" y="132" width="18" height="30" rx="9" fill={SK3} />
                <ellipse cx="33" cy="164" rx="9" ry="8" fill={SK3} />

                {/* Body */}
                <rect x="40" y="86" width="72" height="82" rx="14" fill={ORANGE} />
                <rect x="80" y="86" width="32" height="82" rx="10" fill="rgba(0,0,0,0.07)" />
                <rect x="74" y="92" width="4" height="70" rx="2" fill="#D85300" />

                {/* Legs */}
                <rect x="46" y="162" width="26" height="76" rx="12" fill={DK} />
                <rect x="80" y="162" width="26" height="76" rx="12" fill={DK} />
                <rect x="80" y="162" width="26" height="76" rx="12" fill="rgba(0,0,0,0.04)" />

                {/* Shoes */}
                <ellipse cx="59" cy="242" rx="16" ry="8" fill="#1A1A2E" />
                <ellipse cx="93" cy="242" rx="16" ry="8" fill="#1A1A2E" />

                {/* Right arm */}
                <rect x="106" y="96" width="22" height="44" rx="11" fill={ORANGE} />
                <rect x="108" y="132" width="18" height="30" rx="9" fill={SK3} />
                <ellipse cx="117" cy="164" rx="9" ry="8" fill={SK3} />

                {/* Neck */}
                <rect x="68" y="68" width="18" height="20" rx="4" fill={SK3} />

                {/* Head */}
                <ellipse cx="77" cy="44" rx="24" ry="26" fill={SK3} />
                <ellipse cx="53" cy="48" rx="4" ry="6" fill={SK3} />
                <ellipse cx="101" cy="48" rx="4" ry="6" fill={SK3} />

                {/* Hair */}
                <ellipse cx="77" cy="22" rx="26" ry="14" fill={HR3} />
                <ellipse cx="65" cy="28" rx="13" ry="12" fill={HR3} />
                <ellipse cx="89" cy="26" rx="11" ry="11" fill={HR3} />

                {/* Face */}
                <circle cx="68" cy="44" r="3" fill={EYE} />
                <circle cx="70" cy="42" r="1" fill={W} />
                <circle cx="86" cy="44" r="3" fill={EYE} />
                <circle cx="88" cy="42" r="1" fill={W} />
                <ellipse cx="62" cy="52" rx="6" ry="3" fill="rgba(232,93,117,0.18)" />
                <ellipse cx="92" cy="52" rx="6" ry="3" fill="rgba(232,93,117,0.18)" />
                <rect x="73" y="54" width="8" height="4" rx="2" fill={PINK} />
                <rect x="75" y="54" width="4" height="2" rx="1" fill={W} />
            </motion.g>
        </svg>
    );
}

/* ═══ MONEY COIN ═══ */
function MoneyCoin({ delay = 0 }) {
    return (
        <motion.svg viewBox="0 0 60 60" className="ref-coin"
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 18, delay }}
        >
            <circle cx="30" cy="30" r="26" fill="#FFD700" />
            <circle cx="30" cy="30" r="22" fill="#FFEC4D" />
            <circle cx="30" cy="30" r="18" fill="#FFD700" />
            <text x="30" y="37" textAnchor="middle" fontSize="20" fontWeight="900" fill="#B8860B" fontFamily="Nunito, sans-serif">$</text>
        </motion.svg>
    );
}

/* ═══ LINK VISUAL — animated chain link ═══ */
function LinkVisual() {
    return (
        <motion.svg viewBox="0 0 120 50" className="ref-link-icon"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20, delay: 0.4 }}
        >
            {/* Left chain */}
            <rect x="8" y="16" width="48" height="18" rx="9" fill={TEAL} />
            <rect x="14" y="20" width="36" height="10" rx="5" fill="rgba(255,255,255,0.2)" />
            {/* Right chain */}
            <rect x="64" y="16" width="48" height="18" rx="9" fill={TEAL} />
            <rect x="70" y="20" width="36" height="10" rx="5" fill="rgba(255,255,255,0.2)" />
            {/* Overlap */}
            <rect x="42" y="14" width="36" height="22" rx="11" fill="#15A89D" />
            <motion.rect x="48" y="18" width="24" height="14" rx="7" fill="rgba(255,255,255,0.15)"
                animate={{ opacity: [0.15, 0.4, 0.15] }}
                transition={{ duration: 1.5, repeat: Infinity }}
            />
        </motion.svg>
    );
}

/* ═══ SCENE CONTENTS ═══ */
function RefSceneContent({ stepId }) {
    switch (stepId) {
        case 'share':
            return (
                <div className="ref-scene__layout">
                    <div className="ref-scene__character">
                        <motion.div className="ref-bubble ref-bubble--share"
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.5 }}
                        >
                            <span className="ref-bubble__emoji">🔗</span>
                            <span>Mira esta app!</span>
                        </motion.div>
                        <PersonSharing animate={true} />
                    </div>

                    <div className="ref-scene__middle">
                        <LinkVisual />
                        <motion.div className="ref-scene__link-preview"
                            initial={{ y: 10, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.7 }}
                        >
                            <div className="ref-link-card">
                                <div className="ref-link-card__icon">🍔</div>
                                <div className="ref-link-card__text">
                                    <span className="ref-link-card__title">JAMA Delivery</span>
                                    <span className="ref-link-card__url">jamaec.com/ref/tu-link</span>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    <div className="ref-scene__character">
                        <motion.div className="ref-bubble"
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.8 }}
                        >
                            <span className="ref-bubble__emoji">👀</span>
                            <span>A ver...</span>
                        </motion.div>
                        <PersonOrdering animate={true} />
                    </div>
                </div>
            );

        case 'order':
            return (
                <div className="ref-scene__layout">
                    <div className="ref-scene__character">
                        <motion.div className="ref-bubble"
                            style={{ left: '10%', top: '-40px' }}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'spring', delay: 0.4 }}
                        >
                            <span className="ref-bubble__emoji">😎</span>
                            <span>Esperando...</span>
                        </motion.div>
                        <PersonSharing animate={true} />
                    </div>

                    <motion.div className="ref-scene__phone-order"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: 'spring', delay: 0.3 }}
                    >
                        <div className="ref-order-phone">
                            <div className="ref-order-phone__notch" />
                            <div className="ref-order-phone__header">
                                <img src="/logo_jama_sinfondo.png" alt="JAMA" className="ref-order-phone__logo" />
                            </div>
                            <div className="ref-order-phone__item">
                                <span>🍕</span>
                                <span className="ref-order-phone__name">Pizza Familiar</span>
                                <span className="ref-order-phone__price">$12.99</span>
                            </div>
                            <div className="ref-order-phone__item">
                                <span>🥤</span>
                                <span className="ref-order-phone__name">Coca-Cola</span>
                                <span className="ref-order-phone__price">$1.50</span>
                            </div>
                            <div className="ref-order-phone__total">
                                <span>Total</span>
                                <span>$14.49</span>
                            </div>
                            <motion.div className="ref-order-phone__btn"
                                animate={{ scale: [1, 1.04, 1] }}
                                transition={{ duration: 1.5, repeat: Infinity }}
                            >Pedir ahora</motion.div>
                            <div className="ref-order-phone__ref-tag">
                                <span>Via referido de</span>
                                <strong>Tu link</strong>
                            </div>
                        </div>
                    </motion.div>

                    <div className="ref-scene__character">
                        <motion.div className="ref-bubble"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'spring', delay: 0.6 }}
                        >
                            <span className="ref-bubble__emoji">🛒</span>
                            <span>Pidiendo!</span>
                        </motion.div>
                        <PersonOrdering animate={true} />
                    </div>
                </div>
            );

        case 'earn':
            return (
                <div className="ref-scene__layout ref-scene__layout--earn">
                    <div className="ref-scene__character">
                        <motion.div className="ref-bubble ref-bubble--money"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'spring', delay: 0.3 }}
                        >
                            <span className="ref-bubble__emoji">🤑</span>
                            <span>+$0.72!</span>
                        </motion.div>
                        <PersonSharing animate={true} />
                    </div>

                    <div className="ref-scene__earnings">
                        {/* Floating coins */}
                        <div className="ref-scene__coins">
                            {[0, 1, 2].map(i => (
                                <motion.div key={i}
                                    animate={{ y: [-4, -18, -4], opacity: [0.6, 1, 0.6] }}
                                    transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
                                >
                                    <MoneyCoin delay={0.4 + i * 0.15} />
                                </motion.div>
                            ))}
                        </div>

                        {/* Earnings card */}
                        <motion.div className="ref-earn-card"
                            initial={{ scale: 0.85, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ type: 'spring', delay: 0.5 }}
                        >
                            <div className="ref-earn-card__header">Tu ganancia</div>
                            <div className="ref-earn-card__amount">
                                <span className="ref-earn-card__pct">5%</span>
                                <span className="ref-earn-card__of">de $14.49</span>
                            </div>
                            <div className="ref-earn-card__total">$0.72</div>
                            <div className="ref-earn-card__divider" />
                            <div className="ref-earn-card__row">
                                <span>Este mes</span>
                                <strong>$48.30</strong>
                            </div>
                            <div className="ref-earn-card__row">
                                <span>Referidos activos</span>
                                <strong>12</strong>
                            </div>
                            <motion.div className="ref-earn-card__bar-row"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.9 }}
                            >
                                <span>Sin límite</span>
                                <div className="ref-earn-card__bar">
                                    <motion.div className="ref-earn-card__bar-fill"
                                        initial={{ width: 0 }}
                                        animate={{ width: '100%' }}
                                        transition={{ duration: 1.2, delay: 1.0 }}
                                    />
                                </div>
                                <span>∞</span>
                            </motion.div>
                        </motion.div>
                    </div>

                    <div className="ref-scene__character">
                        <motion.div className="ref-bubble"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'spring', delay: 0.5 }}
                        >
                            <span className="ref-bubble__emoji">😋</span>
                            <span>Que rico!</span>
                        </motion.div>
                        <PersonOrdering animate={true} />
                    </div>
                </div>
            );

        default:
            return null;
    }
}

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */
const sceneVariants = {
    enter: { opacity: 0, x: 40 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -40 },
};

export default function JamaReferralSection() {
    const [currentStep, setCurrentStep] = useState(0);
    const [isPlaying, setIsPlaying] = useState(true);
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => setIsVisible(entry.isIntersecting),
            { threshold: 0.2 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!isPlaying || !isVisible) return;
        const timer = setTimeout(() => {
            setCurrentStep((prev) => (prev + 1) % steps.length);
        }, STEP_DURATION);
        return () => clearTimeout(timer);
    }, [currentStep, isPlaying, isVisible]);

    const step = steps[currentStep];

    return (
        <section className="ref-section" id="features" ref={sectionRef}>
            <div className="ref-section__inner">

                {/* Header */}
                <div className="ref-section__header">
                    <span className="ref-section__pre">FUNCIONALIDAD ESTRELLA</span>
                    <h2 className="ref-section__title">Link de Referidos</h2>
                    <p className="ref-section__subtitle">
                        Comparte tu link, tus amigos piden, <strong>tú ganas el 5%</strong> de cada pedido. Sin límite.
                    </p>
                </div>

                {/* Step indicators */}
                <div className="ref-steps">
                    {steps.map((s, i) => (
                        <button
                            key={s.id}
                            className={`ref-step ${i === currentStep ? 'ref-step--active' : ''} ${i < currentStep ? 'ref-step--done' : ''}`}
                            onClick={() => setCurrentStep(i)}
                        >
                            <span className="ref-step__num">{s.icon}</span>
                            <span className="ref-step__label">{s.label}</span>
                        </button>
                    ))}
                    <div className="ref-steps__line" aria-hidden="true">
                        <div
                            className="ref-steps__line-fill"
                            style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
                        />
                    </div>
                </div>

                {/* Animated scene */}
                <div className="ref-stage" onClick={() => setIsPlaying(p => !p)}>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={step.id}
                            className="ref-scene"
                            variants={sceneVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                        >
                            <RefSceneContent stepId={step.id} />
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* Bottom CTA */}
                <div className="ref-section__cta">
                    <span className="ref-section__btn ref-section__btn--soon" aria-disabled="true">
                        Ingresa a tu app favorita y empieza
                    </span>
                    <p className="ref-section__note">Dinero real. Cobros automáticos. Sin límite de ganancias.</p>
                </div>

            </div>
        </section>
    );
}
